import { AsyncButton, Button } from '@components/button';
import { Form, FormFileUpload, FormTextInput } from '@components/form';
import { useFileUpload } from '@fileUpload/FileUpload';
import { useUploadDatasetFinalizerMutation, useUploadDatasetMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { useState } from 'react';

interface DatasetUploadModalProps {
  workspaceId: string;
  onClose: () => void;
}

export const DatasetsActionUploadModal = ({ workspaceId, onClose }: DatasetUploadModalProps) => {
  const [isUploadStarted, setIsUploadStarted] = useState<boolean>(false);

  const { createAsyncNotification } = useNotifications();

  const [uploadDataset] = useUploadDatasetMutation();
  const [uploadDatasetFinalizer] = useUploadDatasetFinalizerMutation();
  const uploadFile = useFileUpload();

  const uploadDatasetAsyncFunction = createAsyncNotification(
    'Dataset uploaded successfully and will be available soon.',
    async ({ description, file }: { description: string; file: File }) => {
      if (!file) {
        return;
      }
      const { name } = file;

      const { data } = await uploadDataset({
        variables: {
          workspaceId,
          name,
          description,
          size: file.size,
        },
      });

      if (!data?.uploadDataset?.urls) {
        return;
      }

      onClose();

      const etags = await uploadFile({
        file,
        urls: data?.uploadDataset?.urls,
        partSize: data?.uploadDataset?.partSize,
      });

      await uploadDatasetFinalizer({
        variables: {
          parts: etags,
          key: data?.uploadDataset?.key,
          uploadId: data?.uploadDataset?.uploadId,
        },
      });
    },
    { loadingMessage: 'Uploading the dataset...' },
  );

  const submitDataset = async (formData: { description: string; file: File }) => {
    setIsUploadStarted(true);
    await uploadDatasetAsyncFunction(formData).then(() => {});
    setIsUploadStarted(false);
  };

  return (
    <Form initialValues={{ description: '', file: null }} onSubmit={submitDataset}>
      {({ handleSubmit, values }) => (
        <Stack>
          <FormFileUpload name="file" label="dataset file" />
          <FormTextInput label="Description" name="description" multiline rows={3} />
          <Stack gap={4}>
            <AsyncButton
              disabled={!values?.file || isUploadStarted}
              fullWidth
              type="submit"
              data-cy="Datasets-Action-Upload-Modal-Upload-Button"
              onClick={handleSubmit}
            >
              Upload
            </AsyncButton>
            <Button
              fullWidth
              variant="secondary"
              onClick={onClose}
              data-cy="Datasets-Action-Upload-Modal-Cancel-Button"
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};
